import { useState, type CSSProperties } from 'react';
import type { SocialPostPreviewMedia } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useTranslation } from '../../../../lib/i18n';
import { postAuthorName, postCaptionDirection, postNaturalAspect, postTitleAndBody, POST_UNMEASURED_VIDEO } from '../../../../lib/social-post-look-parts';
import { YOUTUBE_DESCRIPTION_LIMIT, YOUTUBE_TITLE_LIMIT } from '../../../../lib/youtube-post-preview';
import { socialBadgeColor } from '../../ui';
import { InlineMediaTile, POST_LOOK_KEYS, PostAvatar, PostCaption, TallFrame, TallMedia, useMediaLabel, type SocialPostLookProps } from './SocialPostLookParts';
import styles from './YoutubePostPreview.module.css';

function Subscribe() {
  const { t } = useTranslation();
  return (
    <span className={styles.subscribe} style={{ backgroundColor: socialBadgeColor('YOUTUBE') }} aria-hidden="true" data-testid="youtube-subscribe">
      {t(POST_LOOK_KEYS.subscribe)}
    </span>
  );
}

export function YoutubeVideo({ card, onOpenMedia, onToggleCaption }: SocialPostLookProps) {
  const { t } = useTranslation();
  const label = useMediaLabel(card);
  const media = card.media[0];
  if (!media) {
    return null;
  }
  const { title, body } = postTitleAndBody(card.caption, YOUTUBE_TITLE_LIMIT);
  return (
    <>
      <YoutubePlayer key={media.url} media={media} label={label(media, 0)} onOpen={() => onOpenMedia(media, 0)} />
      {title ? (
        <PostCaption caption={title} className={styles.title} testId="youtube-title" cut={{ limit: YOUTUBE_TITLE_LIMIT }} onToggle={onToggleCaption} />
      ) : (
        <p className={styles.title} data-testid="youtube-title">
          {t(POST_LOOK_KEYS.videoTitle)}
        </p>
      )}
      <div className={styles.channel}>
        <div className={styles.channelWho}>
          <PostAvatar card={card} name={postAuthorName(card)} size={30} />
          <span className={styles.channelName} data-testid="youtube-channel-name">
            <bdi>{postAuthorName(card)}</bdi>
          </span>
        </div>
        <Subscribe />
      </div>
      {body && (
        <PostCaption
          caption={body}
          className={styles.description}
          testId="youtube-description"
          cut={{ limit: YOUTUBE_DESCRIPTION_LIMIT }}
          onToggle={onToggleCaption}
        />
      )}
    </>
  );
}

function YoutubePlayer({ media, label, onOpen }: { media: SocialPostPreviewMedia; label: string; onOpen: () => void }) {
  const [failed, setFailed] = useState(false);
  const [aspect, setAspect] = useState<number | undefined>(undefined);
  const frame: CSSProperties = { aspectRatio: String(aspect ?? POST_UNMEASURED_VIDEO) };
  return (
    <div className={styles.player} style={frame} data-testid="youtube-player" data-failed={failed}>
      <InlineMediaTile
        media={media}
        label={label}
        fit="contain"
        onMeasure={(width, height) => setAspect(postNaturalAspect(width, height))}
        onFail={() => setFailed(true)}
        onOpen={onOpen}
      />
    </div>
  );
}

export function YoutubeShort({ card, onOpenMedia, onToggleCaption }: SocialPostLookProps) {
  const { t } = useTranslation();
  const label = useMediaLabel(card);
  const [fits, setFits] = useState(false);
  const media = card.media[0];
  if (!media) {
    return null;
  }
  const { title, body } = postTitleAndBody(card.caption, YOUTUBE_TITLE_LIMIT);
  return (
    <div className={styles.short} data-testid="youtube-short">
      <TallFrame fits={fits}>
        <TallMedia key={media.url} media={media} label={label(media, 0)} onOpen={() => onOpenMedia(media, 0)} onFit={setFits} />
        <div className={styles.shortOverlay}>
          {title ? (
            <span className={styles.shortTitle} data-testid="youtube-title">
              <span dir={postCaptionDirection(title) ?? 'auto'}>{title}</span>
            </span>
          ) : body ? (
            <PostCaption caption={body} className={styles.shortTitle} testId="youtube-title" cut={{ limit: YOUTUBE_TITLE_LIMIT }} onMedia onToggle={onToggleCaption} />
          ) : (
            <span className={styles.shortTitle} data-testid="youtube-title">
              {t(POST_LOOK_KEYS.videoTitle)}
            </span>
          )}
          <div className={styles.shortChannel}>
            <div className={styles.shortWho}>
              <PostAvatar card={card} name={postAuthorName(card)} size={30} />
              <span className={styles.shortChannelName} data-testid="youtube-channel-name">
                <bdi>{postAuthorName(card)}</bdi>
              </span>
            </div>
            <Subscribe />
          </div>
        </div>
      </TallFrame>
    </div>
  );
}
